import React from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Undo2, Redo2, Scissors, Trash2, ZoomOut, ZoomIn, Maximize2, Magnet, Combine, RectangleHorizontal } from 'lucide-react';

const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:5'];

/**
 * Area 4 — Editing Toolbar, sitting directly above the Master timeline.
 * Split/Delete/Merge act on the selected timeline item; Undo/Redo extend
 * the existing history pattern to Master timeline state (see
 * UnifiedEditor.jsx's applyMaster/undoMaster/redoMaster). The Aspect
 * Ratio selector sets the whole composition's canvas (width/height),
 * reused identically by the Preview Player and (eventually) rendering.
 */
export function EditorEditingToolbar({
  canUndo, canRedo, onUndo, onRedo,
  hasSelection, onSplit, onDelete, onMerge,
  ripple, onToggleRipple,
  zoom, onZoomChange, onFitZoom,
  snap, onToggleSnap,
  aspectRatio, onAspectRatioChange, aspectRatioLocked,
}) {
  return (
    <div className="flex items-center justify-between gap-3 glass rounded-xl px-3 py-2" data-testid="editor-editing-toolbar">
      <div className="flex items-center gap-1">
        <Button size="icon" variant="outline" className="border-white/10 h-8 w-8" disabled={!canUndo} onClick={onUndo} data-testid="master-undo-button"><Undo2 className="h-4 w-4" /></Button>
        <Button size="icon" variant="outline" className="border-white/10 h-8 w-8" disabled={!canRedo} onClick={onRedo} data-testid="master-redo-button"><Redo2 className="h-4 w-4" /></Button>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <Button size="icon" variant="outline" className="border-white/10 h-8 w-8" disabled={!hasSelection} onClick={onSplit} title="Split at playhead" data-testid="master-split-button"><Scissors className="h-4 w-4" /></Button>
        <Button size="icon" variant="outline" className="border-white/10 h-8 w-8" disabled={!hasSelection} onClick={onMerge} title="Merge with the next adjacent, contiguous clip" data-testid="master-merge-button"><Combine className="h-4 w-4" /></Button>
        <Button size="icon" variant="outline" className="border-white/10 h-8 w-8" disabled={!hasSelection} onClick={onDelete} title="Delete" data-testid="master-delete-button"><Trash2 className="h-4 w-4" /></Button>
        <Button
          size="sm" variant={ripple ? 'default' : 'outline'}
          className={ripple ? 'bg-[#3A7BFF] hover:bg-[#3A7BFF]/90 h-8' : 'border-white/10 h-8'}
          onClick={onToggleRipple} title="Ripple delete — shifts later clips to close the gap"
          data-testid="master-ripple-toggle"
        >
          Ripple
        </Button>
        <Button
          size="sm" variant={snap ? 'default' : 'outline'}
          className={snap ? 'bg-[#3A7BFF] hover:bg-[#3A7BFF]/90 h-8' : 'border-white/10 h-8'}
          onClick={onToggleSnap} title="Snap to half-second grid"
          data-testid="master-snap-toggle"
        >
          <Magnet className="h-4 w-4 mr-1.5" />Snap
        </Button>
      </div>

      <div className="flex items-center gap-2">
        {aspectRatioLocked ? (
          <span
            className="h-8 px-2.5 flex items-center gap-1.5 rounded-md border border-white/10 bg-transparent text-xs text-[#8A93A6]"
            title="Main Editor is fixed at 16:9 for long-form content — pick a different aspect ratio per output in Clips"
            data-testid="master-aspect-ratio-locked"
          >
            <RectangleHorizontal className="h-3.5 w-3.5" />{aspectRatio}
          </span>
        ) : (
          <Select value={aspectRatio} onValueChange={onAspectRatioChange}>
            <SelectTrigger className="h-8 w-24 border-white/10 bg-transparent text-xs" data-testid="master-aspect-ratio-select">
              <RectangleHorizontal className="h-3.5 w-3.5 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>{ASPECT_RATIOS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="w-px h-5 bg-white/10" />
        <Button size="icon" variant="outline" className="border-white/10 h-8 w-8" onClick={() => onZoomChange(Math.max(10, zoom - 15))} data-testid="master-zoom-out-button"><ZoomOut className="h-4 w-4" /></Button>
        <Slider value={[zoom]} min={10} max={240} step={5} onValueChange={([v]) => onZoomChange(v)} className="w-28" data-testid="master-zoom-slider" />
        <Button size="icon" variant="outline" className="border-white/10 h-8 w-8" onClick={() => onZoomChange(Math.min(240, zoom + 15))} data-testid="master-zoom-in-button"><ZoomIn className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" className="border-white/10 h-8" onClick={onFitZoom} data-testid="master-fit-zoom-button"><Maximize2 className="h-3.5 w-3.5 mr-1.5" />Fit</Button>
      </div>
    </div>
  );
}
